'use client';

/**
 * 新建组织页（v2.0新增）
 * 
 * @页面路径 /organization/organizations/new
 * @权限要求 organization:create (全局权限)
 * @文档参考 docs/modules/organization/05-ui-interaction-spec.md
 * 
 * 核心功能：
 * - 创建新的组织实体
 * - 必填字段：组织名称、组织代码、主要区域
 * - 可选字段：描述、法人信息、财务配置等
 * - 支持选择多个运营区域
 * - 表单验证
 */

import { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { useTranslation } from '@/hooks/useTranslation';
import { toast } from '@/lib/toast';
import { FieldMessage } from '@/components/common/feedback/FieldMessage';
import { handleApiError } from '@/lib/error-handler';
import {
  Building2,
  ArrowLeft,
  MapPin,
  FileText,
} from 'lucide-react';
import {
  createOrganization,
  getRegions,
  type CreateOrganizationDto,
  type Region,
} from '@/services/api/organization';

export default function NewOrganizationPage() {
  const router = useRouter();
  const { t } = useTranslation();
  
  const [formData, setFormData] = useState<CreateOrganizationDto>({
    name: '',
    code: '',
    primaryRegionId: '',
    operatingRegionIds: [],
    displayName: '',
  });
  
  const [regions, setRegions] = useState<Region[]>([]);
  const [selectedRegions, setSelectedRegions] = useState<string[]>([]);
  const [primaryRegionId, setPrimaryRegionId] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [loadingRegions, setLoadingRegions] = useState(true);

  useEffect(() => {
    loadRegions();
  }, []);

  const loadRegions = async () => {
    try {
      setLoadingRegions(true);
      const data = await getRegions();
      setRegions(data);
      // 默认选择第一个区域
      if (data.length > 0) {
        setPrimaryRegionId(data[0].id);
        setSelectedRegions([data[0].id]);
      }
    } catch (error: any) {
      handleApiError(error, t.locale, { prefix: t.organization.regions.loadFailed });
    } finally {
      setLoadingRegions(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // 验证
    if (!formData.name.trim()) {
      toast.error(t.organization.orgNameRequired);
      return;
    }
    if (!formData.code.trim()) {
      toast.error(t.organization.orgs.new.codeRequired);
      return;
    }
    if (!primaryRegionId) {
      toast.error(t.organization.selectRegionRequired);
      return;
    }

    try {
      setLoading(true);
      
      // 创建组织
      const orgData: CreateOrganizationDto = {
        name: formData.name.trim(),
        code: formData.code.trim(),
        displayName: formData.name.trim(),
        primaryRegionId: primaryRegionId,
        operatingRegionIds: selectedRegions.length > 0 ? selectedRegions : [primaryRegionId],
      };

      await createOrganization(orgData);
      
      toast.success(t.organization.orgs.messages.createSuccess);
      router.push('/organization/organizations');
    } catch (error: any) {
      handleApiError(error, t.locale, { prefix: t.organization.orgs.messages.createFailed });
    } finally {
      setLoading(false);
    }
  };

  const toggleRegion = (regionId: string) => {
    if (selectedRegions.includes(regionId)) {
      // 如果取消的是主要区域，需要重新选择主要区域
      if (regionId === primaryRegionId && selectedRegions.length > 1) {
        const remaining = selectedRegions.filter(id => id !== regionId);
        setPrimaryRegionId(remaining[0]);
      }
      setSelectedRegions(selectedRegions.filter(id => id !== regionId));
    } else {
      setSelectedRegions([...selectedRegions, regionId]);
      // 如果没有主要区域，自动设置为主要区域
      if (!primaryRegionId) {
        setPrimaryRegionId(regionId);
      }
    }
  };

  return (
    <div className="h-full flex flex-col" style={{ backgroundColor: '#f7f8fa' }}>
      {/* 标题栏 */}
      <div className="bg-white border-b" style={{ borderColor: '#e5e6eb' }}>
        <div className="px-6 h-12 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => router.back()}
              className="p-1 rounded-md transition-colors hover:bg-gray-100"
              data-action="back"
            >
              <ArrowLeft className="w-5 h-5" style={{ color: '#646a73' }} />
            </button>
            <h1 className="text-base font-semibold" style={{ color: '#1f2329' }} data-testid="page-title">
              新建组织
            </h1>
          </div>
        </div>
      </div>

      {/* 表单内容 */}
      <div className="flex-1 overflow-auto px-6 py-6">
        <div className="max-w-3xl mx-auto">
          <form onSubmit={handleSubmit} className="bg-white rounded-lg p-6" style={{ border: '1px solid #e5e6eb' }} data-form="organization">
            {/* 基本信息 */}
            <div className="mb-6">
              <h2 className="text-base font-medium mb-4" style={{ color: '#1f2329' }}>
                基本信息
              </h2>
              
              <div className="space-y-4">
                {/* 组织名称 */}
                <div>
                  <label className="block text-sm font-medium mb-1" style={{ color: '#1f2329' }}>
                    {t.organization.orgs.fields.name} <span style={{ color: '#f53f3f' }}>*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder={t.organization.orgs.new.namePlaceholder}
                    required
                    className="w-full px-3 py-2 rounded-lg focus:outline-none transition-all"
                    style={{
                      border: '1px solid #e5e6eb',
                      color: '#1f2329'
                    }}
                    onFocus={(e) => e.currentTarget.style.borderColor = '#3370ff'}
                    onBlur={(e) => e.currentTarget.style.borderColor = '#e5e6eb'}
                  />
                </div>

                {/* 组织代码 */}
                <div>
                  <label className="block text-sm font-medium mb-1" style={{ color: '#1f2329' }}>
                    {t.organization.orgs.fields.code} <span style={{ color: '#f53f3f' }}>*</span>
                  </label>
                  <input
                    type="text"
                    name="code"
                    value={formData.code}
                    onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                    placeholder={t.organization.orgs.new.codePlaceholder}
                    required
                    className="w-full px-3 py-2 rounded-lg focus:outline-none transition-all"
                    style={{
                      border: '1px solid #e5e6eb',
                      color: '#1f2329'
                    }}
                    onFocus={(e) => e.currentTarget.style.borderColor = '#3370ff'}
                    onBlur={(e) => e.currentTarget.style.borderColor = '#e5e6eb'}
                  />
                  <p className="mt-1 text-xs" style={{ color: '#8f959e' }}>
                    唯一标识符，建议使用大写字母和数字
                  </p>
                </div>

                {/* 显示名称 */}
                <div>
                  <label className="block text-sm font-medium mb-1" style={{ color: '#1f2329' }}>
                    <FileText className="w-4 h-4 inline mr-1" />
                    显示名称（可选）
                  </label>
                  <input
                    type="text"
                    name="displayName"
                    value={formData.displayName || ''}
                    onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
                    placeholder="组织的显示名称（默认使用组织名称）"
                    className="w-full px-3 py-2 rounded-lg focus:outline-none transition-all"
                    style={{
                      border: '1px solid #e5e6eb',
                      color: '#1f2329'
                    }}
                    onFocus={(e) => e.currentTarget.style.borderColor = '#3370ff'}
                    onBlur={(e) => e.currentTarget.style.borderColor = '#e5e6eb'}
                  />
                </div>
              </div>
            </div>

            {/* 区域配置 */}
            <div className="mb-6">
              <h2 className="text-base font-medium mb-4 flex items-center" style={{ color: '#1f2329' }}>
                <MapPin className="w-5 h-5 mr-2 text-blue-600" />
                区域配置
              </h2>

              {loadingRegions ? (
                <div className="flex items-center justify-center py-8">
                  <div className="w-6 h-6 rounded-full animate-spin" style={{ borderTop: '2px solid #3370ff', borderRight: '2px solid transparent' }} />
                </div>
              ) : regions.length === 0 ? (
                <div className="text-center py-8 rounded-lg" style={{ backgroundColor: '#f7f8fa' }}>
                  <FieldMessage
                    variant="warning"
                    message={t.organization.orgs.new.noRegionsAvailable}
                  />
                </div>
              ) : (
                <div className="space-y-4">
                  {/* 主要区域选择 */}
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: '#1f2329' }}>
                      {t.organization.orgs.fields.primaryRegion} <span style={{ color: '#f53f3f' }}>*</span>
                    </label>
                    <select
                      value={primaryRegionId}
                      onChange={(e) => {
                        setPrimaryRegionId(e.target.value);
                        // 确保主要区域在选中区域列表中
                        if (!selectedRegions.includes(e.target.value)) {
                          setSelectedRegions([...selectedRegions, e.target.value]);
                        }
                      }}
                      required
                      className="w-full px-3 py-2 rounded-lg focus:outline-none transition-all"
                      style={{
                        border: '1px solid #e5e6eb',
                        color: '#1f2329'
                      }}
                    >
                      <option value="">{t.organization.orgs.new.primaryRegionPlaceholder}</option>
                      {regions.map((region) => (
                        <option key={region.id} value={region.id}>
                          {region.name} ({region.code})
                        </option>
                      ))}
                    </select>
                    <p className="mt-1 text-xs" style={{ color: '#8f959e' }}>
                      组织的主要运营区域
                    </p>
                  </div>

                  {/* 运营区域选择 */}
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: '#1f2329' }}>
                      运营区域（可多选）
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      {regions.map((region) => {
                        const isSelected = selectedRegions.includes(region.id);
                        const isPrimary = primaryRegionId === region.id;
                        
                        return (
                          <button
                            key={region.id}
                            type="button"
                            onClick={() => toggleRegion(region.id)}
                            className="px-3 py-2 rounded-lg text-sm transition-all flex items-center justify-between"
                            style={{
                              border: isSelected ? '1px solid #3370ff' : '1px solid #e5e6eb',
                              backgroundColor: isSelected ? '#e7f3ff' : '#fff',
                              color: isSelected ? '#3370ff' : '#1f2329'
                            }}
                          >
                            <span>{region.name}</span>
                            {isPrimary && (
                              <span className="ml-2 px-1.5 py-0.5 text-xs rounded" style={{ backgroundColor: '#3370ff', color: '#fff' }}>
                                主
                              </span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                    <p className="mt-2 text-xs" style={{ color: '#8f959e' }}>
                      已选择 {selectedRegions.length} 个区域
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* 操作按钮 */}
            <div className="flex items-center justify-end gap-3 pt-4" style={{ borderTop: '1px solid #e5e6eb' }}>
              <button
                type="button"
                onClick={() => router.back()}
                disabled={loading}
                className="px-4 py-2 rounded-lg transition-colors"
                style={{
                  backgroundColor: '#fff',
                  border: '1px solid #e5e6eb',
                  color: '#1f2329'
                }}
                data-action="cancel"
              >
                取消
              </button>
              <button
                type="submit"
                disabled={loading || loadingRegions || !primaryRegionId}
                className="px-4 py-2 rounded-lg transition-colors flex items-center gap-2"
                style={{
                  backgroundColor: loading ? '#8faeff' : '#3370ff',
                  color: '#fff',
                  border: 'none',
                  opacity: (loading || loadingRegions || !primaryRegionId) ? 0.5 : 1,
                  cursor: (loading || loadingRegions || !primaryRegionId) ? 'not-allowed' : 'pointer'
                }}
                data-action="submit"
              >
                {loading ? (
                  <>
                    <div className="w-4 h-4 rounded-full animate-spin" style={{ borderTop: '2px solid #fff', borderRight: '2px solid transparent' }} />
                    创建中...
                  </>
                ) : (
                  <>
                    <Building2 className="w-4 h-4" />
                    创建组织
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
